/** @jsxImportSource @emotion/react */
import styled from '@emotion/styled';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import React from 'react';
import TeamLogo from 'ud-ui/components/team-logo';
import { AnalyticsMatchRecord } from 'modules/analytics/domain/interfaces/tournament-analytics';

interface MatchRecordCardProps {
  title: string;
  icon: React.ReactNode;
  record: AnalyticsMatchRecord | null;
  highlightLabel: string;
}

const Card = styled.div`
  background: ${({ theme }) => theme.colors.SurfaceWhite};
  border: 1px solid ${({ theme }) => theme.colors.SurfacePlatinum20};
  border-left: 4px solid ${({ theme }) => theme.colors.DarkYellow};
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => theme.colors.DarkBlue};
  font-size: 14px;
  font-weight: 600;

  svg {
    color: ${({ theme }) => theme.colors.DarkYellow};
    flex-shrink: 0;
  }
`;

const EmptyState = styled.div`
  color: ${({ theme }) => theme.colors.Gray};
  font-size: 14px;
  text-align: center;
  padding: 12px 0;
`;

const ScoreRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const TeamSide = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 100px;
`;

const TeamName = styled.span`
  color: ${({ theme }) => theme.colors.DarkBlue};
  font-size: 15px;
  font-weight: 600;
  text-align: center;
`;

const Score = styled.span`
  color: ${({ theme }) => theme.colors.DarkBlue};
  font-size: 22px;
  font-weight: 700;
  white-space: nowrap;
`;

const Footer = styled.div`
  color: ${({ theme }) => theme.colors.WhiteGray};
  font-size: 12px;
  text-align: center;
`;

const MatchRecordCard = React.memo(({ title, icon, record, highlightLabel }: MatchRecordCardProps) => {
  return (
    <Card>
      <TitleRow>
        {icon}
        <span>{title}</span>
      </TitleRow>
      {record === null ? (
        <EmptyState>Нет данных за выбранный период</EmptyState>
      ) : (
        <>
          <ScoreRow>
            <TeamSide>
              <TeamLogo url={record.team1.emblemUrl ?? undefined} name={record.team1.name} size={40} />
              <TeamName>{record.team1.name}</TeamName>
            </TeamSide>
            <Score>
              {record.team1.score} : {record.team2.score}
            </Score>
            <TeamSide>
              <TeamLogo url={record.team2.emblemUrl ?? undefined} name={record.team2.name} size={40} />
              <TeamName>{record.team2.name}</TeamName>
            </TeamSide>
          </ScoreRow>
          <Footer>
            {record.championshipName} · {format(new Date(record.matchDate), 'd MMM yyyy', { locale: ru })} · {highlightLabel}:{' '}
            {record.highlightValue}
          </Footer>
        </>
      )}
    </Card>
  );
});

export default MatchRecordCard;
